export type AiSessionFilter = 'active' | 'retired' | 'replaced' | 'cleanup';

export interface AiSessionRecord {
  readonly id: string;
  readonly project_id: string;
  readonly worker_id: string;
  readonly node_id: string;
  readonly offering_key: string;
  readonly generation: number;
  readonly status: string;
  readonly lifecycle: string;
  readonly replacement_session_id: string | null;
  readonly retired_at: string | null;
  readonly provider_cleanup_state: string | null;
}

export interface AiSessionAssignmentRecord {
  readonly id: string;
  readonly run_id: string;
  readonly session_id: string;
  readonly active: boolean;
}

export interface AiSessionRunRecord {
  readonly id: string;
  readonly task_id: string;
  readonly status: string;
}

export interface AiSessionTaskRecord {
  readonly id: string;
  readonly title: string;
  readonly status: string;
}

export interface AiSessionWorkerRecord {
  readonly id: string;
  readonly number: number;
  readonly name: string;
}

export interface AiSessionNodeRecord {
  readonly node_id: string;
  readonly name: string;
}

export interface AiSessionCatalogRow {
  readonly id: string;
  readonly projectName: string;
  readonly workerLabel: string;
  readonly nodeName: string;
  readonly offeringKey: string;
  readonly generation: number;
  readonly status: string;
  readonly lifecycle: string;
  readonly replacementSessionId: string | null;
  readonly retiredAt: string | null;
  readonly cleanupState: string | null;
  readonly runId: string | null;
  readonly runStatus: string | null;
  readonly taskTitle: string | null;
  readonly taskStatus: string | null;
}

export interface AiSessionCatalogInput {
  readonly projectId: string;
  readonly projectName: string;
  readonly sessions: readonly AiSessionRecord[];
  readonly assignments: readonly AiSessionAssignmentRecord[];
  readonly runs: readonly AiSessionRunRecord[];
  readonly tasks: readonly AiSessionTaskRecord[];
  readonly workers: readonly AiSessionWorkerRecord[];
  readonly nodes: readonly AiSessionNodeRecord[];
}

function relevantAssignments(rows: readonly AiSessionAssignmentRecord[]): ReadonlyMap<string, AiSessionAssignmentRecord> {
  const grouped = new Map<string, AiSessionAssignmentRecord[]>();
  for (const row of rows) {
    const group = grouped.get(row.session_id) ?? [];
    group.push(row);
    grouped.set(row.session_id, group);
  }
  const relevant = new Map<string, AiSessionAssignmentRecord>();
  for (const [sessionId, group] of grouped) {
    const active = group.filter((row) => row.active);
    // The schema guarantees at most one active assignment per session. When a
    // retired session has several inactive historical runs, there is no date
    // on the assignment that proves which was latest, so leave it Unknown.
    const exact = active.length === 1 ? active[0] : (group.length === 1 ? group[0] : undefined);
    if (exact !== undefined) relevant.set(sessionId, exact);
  }
  return relevant;
}

/**
 * Join the existing workforce-session lifecycle to its most relevant recorded
 * run and task. This is a read model only; it creates no second session store.
 */
export function buildAiSessionCatalog(input: AiSessionCatalogInput): readonly AiSessionCatalogRow[] {
  const workers = new Map(input.workers.map((row) => [row.id, row]));
  const nodes = new Map(input.nodes.map((row) => [row.node_id, row]));
  const assignments = relevantAssignments(input.assignments);
  const runs = new Map(input.runs.map((row) => [row.id, row]));
  const tasks = new Map(input.tasks.map((row) => [row.id, row]));

  return input.sessions
    .filter((session) => session.project_id === input.projectId)
    .map((session) => {
      const worker = workers.get(session.worker_id);
      const assignment = assignments.get(session.id);
      const run = assignment === undefined ? undefined : runs.get(assignment.run_id);
      const task = run === undefined ? undefined : tasks.get(run.task_id);
      return {
        id: session.id,
        projectName: input.projectName,
        workerLabel: worker === undefined ? 'Unknown' : `Worker ${worker.number} · ${worker.name}`,
        nodeName: nodes.get(session.node_id)?.name ?? 'Unknown',
        offeringKey: session.offering_key,
        generation: session.generation,
        status: session.status,
        lifecycle: session.lifecycle,
        replacementSessionId: session.replacement_session_id,
        retiredAt: session.retired_at,
        cleanupState: session.provider_cleanup_state,
        runId: run?.id ?? null,
        runStatus: run?.status ?? null,
        taskTitle: task?.title ?? null,
        taskStatus: task?.status ?? null,
      };
    })
    .sort((a, b) => b.generation - a.generation || a.workerLabel.localeCompare(b.workerLabel));
}

export function aiSessionMatches(row: AiSessionCatalogRow, filter: AiSessionFilter): boolean {
  switch (filter) {
    case 'active':
      return row.lifecycle !== 'retired' && row.retiredAt === null;
    case 'retired':
      return row.lifecycle === 'retired' || row.retiredAt !== null;
    case 'replaced':
      return row.replacementSessionId !== null;
    case 'cleanup':
      return row.cleanupState === 'pending' || row.cleanupState === 'unsupported';
  }
}

export function aiSessionFilterCounts(rows: readonly AiSessionCatalogRow[]): Readonly<Record<AiSessionFilter, number>> {
  return {
    active: rows.filter((row) => aiSessionMatches(row, 'active')).length,
    retired: rows.filter((row) => aiSessionMatches(row, 'retired')).length,
    replaced: rows.filter((row) => aiSessionMatches(row, 'replaced')).length,
    cleanup: rows.filter((row) => aiSessionMatches(row, 'cleanup')).length,
  };
}

export function cleanupStateLabel(value: string | null): string {
  if (value === 'pending') return 'Cleanup requested';
  if (value === 'done') return 'Cleanup recorded complete';
  if (value === 'unsupported') return 'Cleanup unsupported';
  return 'Unknown';
}

export function shortSessionId(value: string): string {
  return value.length <= 12 ? value : `${value.slice(0, 8)}…`;
}
